import { Box, Typography } from "@mui/material";
import React from "react";
import "./ServicesBox.css";
import "../CSS/Background.css";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FireExtinguisherIcon from "@mui/icons-material/FireExtinguisher";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ParkIcon from "@mui/icons-material/Park";
import Button from "@mui/material/Button";

export default function ServiceButton() {
  // Structured data for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Christmas Tree Services",
    "description": "Professional Christmas tree services including delivery, setup, flocking, fire retardant treatment, and recycling",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "Christmas Tree Delivery and Setup",
          "description": "Professional delivery and setup service for real Christmas trees starting at $45",
          "provider": {
            "@type": "Organization",
            "name": "Your Christmas Tree Company"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Fire Retardant Treatment",
          "description": "Fire retardant treatment for Christmas trees at $5 per foot, required for commercial locations",
          "provider": {
            "@type": "Organization",
            "name": "Your Christmas Tree Company"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Service",
          "name": "Christmas Tree Recycling",
          "description": "Eco-friendly tree removal and recycling service starting at $45",
          "provider": {
            "@type": "Organization",
            "name": "Your Christmas Tree Company"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Service",
          "name": "Phone and Email Orders",
          "description": "Convenient phone and email ordering service available Monday to Friday",
          "provider": {
            "@type": "Organization",
            "name": "Your Christmas Tree Company"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "Service",
          "name": "Christmas Tree Flocking",
          "description": "Professional white flocking service with light, medium, heavy, and tips-only options",
          "provider": {
            "@type": "Organization",
            "name": "Your Christmas Tree Company"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 6,
        "item": {
          "@type": "Service",
          "name": "Credit Card Payment Options",
          "description": "Secure credit card payments accepted for all Christmas tree services",
          "provider": {
            "@type": "Organization",
            "name": "Your Christmas Tree Company"
          }
        }
      }
    ]
  };

  return (
    <>
      {/* Structured Data JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      <Box
        component="section"
        className="background"
        aria-label="Christmas Tree Services and Features"
        sx={{
          display: "flex",
          justifyContent: "space-around",
          flexWrap: "wrap",
          color: "text.main",
          paddingTop: "16px",
          paddingBottom: "16px",
        }}
      >
        {/* Hidden H1 for SEO */}
        <h1 className="visually-hidden">
          Christmas Tree Services: Delivery, Flocking, Fire Retardant, and Recycling
        </h1>

        <Box className="box-row">
          {/*-----------Delivery box start-----------*/}
          <Box
            component="article"
            className="ServiceBox"
            backgroundColor="secondary.main"
            boxShadow={10}
            itemScope
            itemType="https://schema.org/Service"
          >
            <Box className="service-header">
              <LocalShippingIcon className="header-text" aria-hidden="true" />
              <Typography component="h2" className="header-text" itemProp="name">
                Tree Delivery
              </Typography>
            </Box>
            <Typography className="ServiceBody" itemProp="description">
              If you want to enjoy the festive spirit of a real Christmas tree
              without the hassle of transporting and setting it up yourself, we
              have the perfect solution for you. Starting at $45, our delivery and
              setup service will bring your chosen tree to your door and install
              it securely in your preferred spot.
            </Typography>
          </Box>
          {/*-----------Delivery box end-----------*/}
          {/*-----------fire retardant box start-----------*/}
          <Box
            component="article"
            className="ServiceBox"
            backgroundColor="secondary.main"
            boxShadow={10}
            itemScope
            itemType="https://schema.org/Service"
          >
            <Box
              className="service-header"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "solid",
                borderWidth: "2px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <FireExtinguisherIcon className="header-text" aria-hidden="true" />
              <Typography component="h2" className="header-text" itemProp="name">
                Fire Retardant
              </Typography>
            </Box>
            <Typography className="ServiceBody" itemProp="description">
              Fire-retardant-treated Christmas trees are required in public places
              like hotels, restaurants, banks, and offices. Our team applies a
              clear, non-toxic liquid treatment that dries within hours and
              creates a barrier to make it more difficult for the tree to catch
              fire. We also provide display tags with our treatment. The cost of
              our treatment is $5 per foot.
            </Typography>
          </Box>
          {/*-----------fire retardant box end-----------*/}
        </Box>
        <Box className="box-row">
          {/*-----------tree removal box start-----------*/}
          <Box
            component="article"
            className="ServiceBox"
            backgroundColor="secondary.main"
            boxShadow={10}
            itemScope
            itemType="https://schema.org/Service"
          >
            <Box
              className="service-header"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "solid",
                borderWidth: "2px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <DeleteIcon className="header-text" aria-hidden="true" />
              <Typography component="h2" className="header-text" itemProp="name">
                Tree Recycle
              </Typography>
            </Box>
            <Typography className="ServiceBody" itemProp="description">
              Let us take care of your post-season tree removal needs by
              transporting it to a recycling center, where it will be chipped and
              reused as mulch or firewood. Our service starts at just $45 and
              ensures your tree is disposed of in an environmentally friendly way.
            </Typography>
          </Box>
          {/*-----------Tree removal box end-----------*/}
          {/*-----------Phone Orders box start-----------*/}
          <Box
            component="article"
            className="ServiceBox"
            backgroundColor="secondary.main"
            boxShadow={10}
            itemScope
            itemType="https://schema.org/Service"
          >
            <Box
              className="service-header"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "solid",
                borderWidth: "2px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <LocalPhoneIcon className="header-text" aria-hidden="true" />
              <Typography component="h2" className="header-text" itemProp="name">
                Phone and Email Orders
              </Typography>
            </Box>
            <Typography className="ServiceBody" itemProp="description">
  If you would like to order a Christmas tree by phone or email, we are happy to help.
  Simply give us a call or send us an email, and we’ll take care of everything for you.
  <br /><br />
  Subject to non refundable $15 service fee and delivery fee.
</Typography>


          </Box>
          {/*-----------Phone Orders box end-----------*/}
        </Box>
        <Box className="box-row">
          {/*-----------Tree Flocking box start-----------*/}
          <Box
            component="article"
            className="ServiceBox"
            backgroundColor="secondary.main"
            boxShadow={10}
            itemScope
            itemType="https://schema.org/Service"
          >
            <Box
              className="service-header"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "solid",
                borderWidth: "2px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <AcUnitIcon className="header-text" aria-hidden="true" />
              <Typography component="h2" className="header-text" itemProp="name">
                Tree Flocking
              </Typography>
            </Box>
            <Typography className="ServiceBody" itemProp="description">
              Nothing says Christmas like a flocked tree. With its snowy white
              branches, a flocked tree will transform your home into a winter
              wonderland. We only use white flocking, but you can choose from four
              different options: light, medium, heavy, or tips only. Please note
              that flocked trees require a drying period of 1-2 days depending on
              the weather conditions.
            </Typography>
          </Box>
          {/*-----------Tree Flocking box end-----------*/}
          {/*-----------Credit Card box start-----------*/}
          <Box
            component="article"
            className="ServiceBox"
            backgroundColor="secondary.main"
            boxShadow={10}
            itemScope
            itemType="https://schema.org/Service"
          >
            <Box
              className="service-header"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "solid",
                borderWidth: "2px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <CreditCardIcon className="header-text" aria-hidden="true" />
              <Typography component="h2" className="header-text" itemProp="name">
                Credit Card Payments
              </Typography>
            </Box>
            <Typography className="ServiceBody" itemProp="description">
              We accept all major credit cards for your convenience. Pay securely 
              online or over the phone with Visa, Mastercard, American Express, 
              and Discover. Our payment system is fully encrypted to ensure your 
              information stays safe and secure throughout the transaction process.
            </Typography>
          </Box>
          {/*-----------Credit Card box end-----------*/}
          {/*-----------Decoration box start-----------}
          <Box
            className="ServiceBox"
            backgroundColor="secondary.main"np[]
            boxShadow={10}
          >
            <Box
              className="service-header"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "solid",
                borderWidth: "2px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <ParkIcon className="header-text" />
              <Typography className="header-text">Tree Decoration</Typography>
            </Box>
            <Typography className="ServiceBody">
              Let us take the stress out of decorating your Christmas tree this
              year by allowing our team to expertly decorate it for you. With our
              tree decorating services, you can sit back and relax while we
              transform your tree into a stunning holiday centerpiece that you and
              your loved ones will enjoy all season long.
            </Typography>
          </Box>
          {/*-----------Decoration box end-----------*/}
        </Box>
      </Box>
    </>
  );
}